import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, ClipboardCheck, Wrench, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SchedulePicker, scheduleSummary } from "@/components/SchedulePicker";
import { PageHeader } from "@/pages/admin/AdminLayout";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);
const num = (v) => (v === "" ? 0 : Number(v));

function Counts({ c, prefix }) {
  return (
    <div className="flex gap-2 text-[11px]" data-testid={`${prefix}-counts`}>
      <span className="rounded-full bg-[#a2ca6b]/20 px-2 py-0.5 text-[#2f5212]">{c?.month || 0} questo mese</span>
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">{c?.pending || 0} in attesa</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{c?.total || 0} totali</span>
    </div>
  );
}

function TypeCard({ t, prefix, counts, defaults, onChange, onRemove, fixed }) {
  const Icon = fixed ? ClipboardCheck : Wrench;
  const inactive = !fixed && !t.active;
  return (
    <section className={`rounded-2xl border bg-white p-5 transition-opacity ${inactive ? "opacity-60" : ""}`} data-testid={prefix}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0f1710] text-[#a2ca6b]"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1 space-y-2">
          {fixed ? <p className="font-display text-lg font-semibold">Sopralluogo</p>
            : <Input value={t.label} onChange={(e) => onChange({ label: e.target.value })} className="h-9 font-semibold" data-testid={`${prefix}-label`} />}
          <Counts c={counts} prefix={prefix} />
        </div>
        {!fixed && (
          <div className="flex items-center gap-1">
            <Switch checked={t.active} onCheckedChange={(v) => onChange({ active: v })} data-testid={`${prefix}-active`} />
            <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`${prefix}-remove`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Diritto di chiamata €</Label>
          <Input type="number" min="0" step="0.5" value={t.call_fee} onChange={(e) => onChange({ call_fee: num(e.target.value) })} data-testid={`${prefix}-callfee`} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Costo orario €/h</Label>
          <Input type="number" min="0" step="0.5" value={t.hourly_rate} onChange={(e) => onChange({ hourly_rate: num(e.target.value) })} data-testid={`${prefix}-rate`} /></div>
      </div>
      {!fixed && <div className="mt-4 space-y-2 border-t pt-4" data-testid={`${prefix}-flags`}>
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm"><b>Dura più giorni</b><span className="block text-xs text-slate-500">nel nuovo intervento chiedi data di inizio e di fine (prorogabile)</span></span>
          <Switch checked={!!t.multi_day} onCheckedChange={(v) => onChange({ multi_day: v })} data-testid={`${prefix}-multiday-switch`} /></label>
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm"><b>Visibile nel modulo di richiesta pubblico</b><span className="block text-xs text-slate-500">se spento compare solo in dashboard</span></span>
          <Switch checked={t.public !== false} onCheckedChange={(v) => onChange({ public: v })} data-testid={`${prefix}-public-switch`} /></label>
      </div>}
      <div className="mt-4 border-t pt-4">
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold">Giorni e orari dedicati</span>
          <Switch checked={!!t.custom_schedule} data-testid={`${prefix}-custom-switch`}
            onCheckedChange={(v) => onChange(v && !t.slots?.length
              ? { custom_schedule: true, slots: defaults.slots, working_days: defaults.working_days }
              : { custom_schedule: v })} />
        </label>
        {t.custom_schedule ? (
          <div className="mt-4"><SchedulePicker days={t.working_days || []} slots={t.slots || []} onChange={onChange} prefix={prefix} /></div>
        ) : (
          <p className="mt-2 text-xs text-slate-500" data-testid={`${prefix}-default-schedule`}>Orari standard: {scheduleSummary(defaults.working_days, defaults.slots)}</p>
        )}
      </div>
    </section>
  );
}

function NewTypeDialog({ open, onOpenChange, onAdd }) {
  const [label, setLabel] = useState("");
  const add = () => {
    if (label.trim().length < 2) return toast.error("Inserisci il nome del tipo");
    onAdd(label.trim());
    setLabel("");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Nuovo tipo di intervento</DialogTitle>
          <DialogDescription>Dopo l'aggiunta imposta prezzi, giorni e orari, poi salva.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2"><Label>Nome</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Es. Addolcitore acqua" onKeyDown={(e) => e.key === "Enter" && add()} data-testid="new-type-name-input" /></div>
        <DialogFooter><Button onClick={add} className="rounded-full" data-testid="new-type-add-button">Aggiungi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceTypes() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/service-types").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="h-5 w-5 animate-spin" />;

  const defaults = { slots: data.default_slots, working_days: data.default_working_days };
  const setEq = (i, patch) => setData((d) => ({ ...d, equipment: d.equipment.map((e, j) => (j === i ? { ...e, ...patch } : e)) }));
  const remove = (i) => {
    const t = data.equipment[i];
    if (data.counts[t.key]?.total) return toast.error("Tipo già usato in alcuni interventi: disattivalo invece di eliminarlo");
    setData((d) => ({ ...d, equipment: d.equipment.filter((_, j) => j !== i) }));
  };
  const add = (label) => {
    let key = slugify(label) || "tipo", n = 2;
    while (data.equipment.some((e) => e.key === key)) key = `${slugify(label)}_${n++}`;
    const base = data.equipment[0] || { call_fee: 0, hourly_rate: 0 };
    setData((d) => ({ ...d, equipment: [...d.equipment, { key, label, call_fee: base.call_fee, hourly_rate: base.hourly_rate,
      active: true, custom_schedule: false, slots: [], working_days: [] }] }));
  };
  const save = async () => {
    setBusy(true);
    try {
      setData((await api.put("/admin/service-types", { equipment: data.equipment, survey: data.survey })).data);
      toast.success("Tipi di intervento salvati");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const active = data.equipment.filter((e) => e.active).length;
  const month = Object.values(data.counts).reduce((s, c) => s + c.month, 0);

  return (
    <div>
      <PageHeader eyebrow="Catalogo servizi" title="Tipi di intervento">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpen(true)} className="rounded-full" data-testid="new-type-button"><Plus className="h-4 w-4" /> Nuovo tipo</Button>
          <Button onClick={save} disabled={busy} className="rounded-full" data-testid="service-types-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
        </div>
      </PageHeader>
      <div className="mb-6 grid grid-cols-3 gap-3 sm:max-w-xl">
        {[["Tipi attivi", active, "types-kpi-active"], ["Disattivati", data.equipment.length - active, "types-kpi-inactive"], ["Interventi del mese", month, "types-kpi-month"]].map(([l, v, id]) => (
          <div key={id} className="rounded-2xl border bg-white p-4" data-testid={id}><p className="font-display text-3xl font-bold">{v}</p><p className="text-xs text-slate-500">{l}</p></div>
        ))}
      </div>
      <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3" data-testid="service-types-list">
        {data.equipment.map((t, i) => (
          <TypeCard key={t.key} t={t} prefix={`type-${t.key}`} counts={data.counts[t.key]} defaults={defaults}
            onChange={(p) => setEq(i, p)} onRemove={() => remove(i)} />
        ))}
        <TypeCard t={data.survey} prefix="type-survey" fixed counts={data.counts.__survey} defaults={defaults}
          onChange={(p) => setData((d) => ({ ...d, survey: { ...d.survey, ...p } }))} />
      </div>
      <NewTypeDialog open={open} onOpenChange={setOpen} onAdd={add} />
    </div>
  );
}
